/* eslint-disable @typescript-eslint/member-ordering */
import { Injectable } from '@angular/core';
import { VetService } from '@app/core/services';
import { AuthService } from '@app/core/services/auth/auth.service';
import { LogginInformationPopinComponent } from '@app/shared/components/loggin-information-popin/loggin-information-popin.component';
import { ActionsEnum, Constants } from '@app/shared/utils';
import {
  formatedAlertDescriptionWithDates,
  isAlertMessageWithFuturDates,
  shouldDisplayAlertInfoPopin,
  shouldDisplayInfoPopin,
} from '@app/shared/utils/static-helpers/blob-market-helper';
import { translateKey } from '@app/shared/utils/static-helpers/translate';
import { setAlert, toggleLoader } from '@app/store/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { RCAlertType } from '@rc/ui';
import { CookieService } from 'ngx-cookie-service';
import { catchError, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AppState } from '../..';
import {
  displayInfoPopin,
  getFeatureTogglesData,
  getFeatureTogglesDataFail,
  getFeatureTogglesDataSuccess,
  getMarket,
  getMarketFail,
  getMarketSuccess,
  getRecoConsentData,
  getRecoConsentDataFail,
  getRecoConsentDataSuccess,
  setInfoAlert,
  setInfoPopin,
} from '../vet.actions';
import { selectClinic, selectMarket, selectVet } from '../vet.selectors';
import { MatDialog } from '@angular/material/dialog';

@Injectable()
export class GetMarketEffects {
  /**
   * Get market effect
   */
  getMarket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getMarket),
      switchMap(({ countryCode }) => this.vetService.getMarket(countryCode)),
      switchMap((market) => [getMarketSuccess({ market }), toggleLoader({ value: false })]),
      catchError((err) => [getMarketFail(err)]),
    ),
  );

  /**
   * Get market fail effect
   */
  getMarketFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getMarketFail),
      tap(() => this.authService.logout()),
    ),
  );

  /**
   * Get reco consent data effect
   */
  getRecoConsentData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getRecoConsentData),
      mergeMap((action) =>
        this.vetService.getRecoConsentData().pipe(
          switchMap((recoConsent) => [getRecoConsentDataSuccess({ countryCode: action.countryCode, recoConsent })]),
          catchError((err) => [getRecoConsentDataFail(err)]),
        ),
      ),
    ),
  );

  /**
   * Get feature toggles data effect
   */
  getFeatureTogglesData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getFeatureTogglesData),
      mergeMap((action) =>
        this.vetService.getFeatureTogglesData().pipe(
          switchMap((featureToggles) => [
            getFeatureTogglesDataSuccess({ customerId: action.customerId, countryCode: action.countryCode, featureToggles }),
          ]),
          catchError((err) => [getFeatureTogglesDataFail(err)]),
        ),
      ),
    ),
  );

  displayInfoPopin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(displayInfoPopin),
      withLatestFrom(this.store$.select(selectMarket), this.store$.select(selectClinic), this.store$.select(selectVet)),
      switchMap(([, market, clinic, vet]) => {
        const isPopinEnabled = localStorage.getItem(Constants.POPIN_INFORMATION_KEY) === ActionsEnum.enable;
        const isDefinitelyDisabled = this.cookieService.get('popin_info_definitely_disabled') === market?.info_popin?.id;
        if (market?.info_popin && isPopinEnabled && shouldDisplayInfoPopin(market.info_popin, isDefinitelyDisabled)) {
          return [setInfoPopin({ clinic: clinic, market: market, vet: vet })];
        } else if (market?.info_popin && isPopinEnabled && shouldDisplayAlertInfoPopin(market.info_popin)) {
          return [setInfoAlert({ clinic: clinic, market: market, vet: vet })];
        } else {
          return [];
        }
      }),
    ),
  );

  setInfoPopin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setInfoPopin),
      switchMap((action) => {
        return this._dialog
          .open(LogginInformationPopinComponent, {
            panelClass: ['small'],
            data: {
              id: action.market.info_popin.id,
              title: action.market.info_popin.title,
              description: isAlertMessageWithFuturDates(action.market.info_popin)
                ? formatedAlertDescriptionWithDates(action.market.info_popin, action.vet.user?.preferredLanguage)
                : translateKey(action.market.info_popin.description),
              link: action.market.info_popin.link,
              textlink: action.market.info_popin.textlink ?? action.market.info_popin.link,
              displaySupport: action.market.info_popin.displaySupport ?? true,
              showCheckbox: action.market.info_popin.showCheckbox,
            },
          })
          .afterClosed()
          .pipe(
            tap(() => {
              localStorage.setItem(Constants.POPIN_INFORMATION_KEY, ActionsEnum.disable);
            }),
            switchMap(() => [toggleLoader({ value: false })]),
          );
      }),
    ),
  );

  setInfoAlert$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setInfoAlert),
      switchMap((action) => {
        return [
          setAlert({
            alert: {
              id: action.market?.info_popin?.id,
              message: isAlertMessageWithFuturDates(action.market.info_popin)
                ? formatedAlertDescriptionWithDates(action.market.info_popin, action.vet.user?.preferredLanguage)
                : translateKey(action.market.info_popin.description),
              alertType: RCAlertType.WARNING,
              dataQa: 'vs-set-info-warning-alert',
            },
          }),
        ];
      }),
      tap(() => localStorage.setItem(Constants.POPIN_INFORMATION_KEY, ActionsEnum.disable)),
    ),
  );

  constructor(
    private readonly store$: Store<AppState>,
    private readonly actions$: Actions,
    private readonly authService: AuthService,
    private readonly _dialog: MatDialog,
    private readonly cookieService: CookieService,
    private readonly vetService: VetService,
  ) {}
}
